import React, { useRef, useState } from 'react';
import { CheckIcon, Loader2Icon, ArrowRightIcon } from 'lucide-react';
import { cargoTypes } from '../data/site';
import { buildQuoteMailtoUrl, readQuoteRequest, QUOTE_RECIPIENT } from '../utils/quoteEmail';
type Status = 'idle' | 'submitting' | 'success';
const fieldClass = 'w-full rounded-lg border border-line bg-white px-4 py-3 text-[15px] text-ink placeholder:text-ink-soft/55 transition-[border-color,box-shadow] duration-150 ease-premium focus:border-ice focus:outline-none focus:ring-2 focus:ring-ice/25';
const labelClass = 'mb-2 block text-[11px] font-bold uppercase tracking-[0.14em] text-ink-soft';
export function ContactForm() {
  const [status, setStatus] = useState<Status>('idle');
  const mailtoRef = useRef<string>('');
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const quote = readQuoteRequest(event.currentTarget);
    const mailtoUrl = buildQuoteMailtoUrl(quote);
    mailtoRef.current = mailtoUrl;
    setStatus('submitting');
    window.setTimeout(() => {
      window.location.href = mailtoUrl;
      setStatus('success');
    }, 400);
  };
  if (status === 'success') {
    return <div className="flex h-full min-h-[30rem] flex-col items-start justify-center rounded-2xl border border-line bg-white p-10 shadow-[0_18px_50px_rgba(11,14,17,0.07)]" data-id="element-78" data-mp="35u9jngfcspj">
        <span className="flex h-12 w-12 items-center justify-center rounded-full bg-ice-100 text-ice" data-id="element-79" data-mp="86rrq01fpmr1">
          <CheckIcon className="h-6 w-6" data-id="element-80" data-mp="36onh9jmiunbs" />
        </span>
        <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight text-ink" data-id="element-81" data-mp="3bm6vwkn4t249">
          Your request is ready to send
        </h3>
        <p className="mt-3 max-w-sm text-sm leading-relaxed text-ink-soft" data-id="element-82" data-mp="3km3pn0odor66">
          We opened a pre-filled email to <span className="font-semibold text-ink" data-id="element-83" data-mp="vujc7qx4p63e">{QUOTE_RECIPIENT}</span> with your
          lane details — press send in your mail app and dispatch will follow up with capacity and pricing.
        </p>
        <div className="mt-8 flex flex-wrap items-center gap-6" data-id="element-84" data-mp="2ww8sk9u1ijmk">
          <a href={mailtoRef.current} className="text-[12px] font-bold uppercase tracking-[0.12em] text-ice transition-colors duration-150 ease-premium hover:text-ice-600" data-id="element-85" data-mp="2wyljvdlou6ca">
            Open email again
          </a>
          <button type="button" onClick={() => setStatus('idle')} className="text-[12px] font-bold uppercase tracking-[0.12em] text-ink-soft transition-colors duration-150 ease-premium hover:text-ink" data-id="element-86" data-mp="30xudmigqa47u">
            Submit another request
          </button>
        </div>
      </div>;
  }
  return <form onSubmit={handleSubmit} className="rounded-2xl border border-line bg-white p-8 shadow-[0_18px_50px_rgba(11,14,17,0.07)] lg:p-10" data-id="element-87" data-mp="2n0e3owz83sbc">
      <div className="grid gap-5 sm:grid-cols-2" data-id="element-88" data-mp="1ugysgm9ef7rd">
        <div data-id="element-89" data-mp="1ufnpuprow61u">
          <label className={labelClass} htmlFor="name" data-id="element-90" data-mp="1pj70xtco1nnp">
            Name
          </label>
          <input id="name" name="name" required autoComplete="name" className={fieldClass} placeholder="Jane Doe" data-id="element-91" data-mp="50whs0r67xm0" />
        </div>
        <div data-id="element-92" data-mp="9rv015vpz5rw">
          <label className={labelClass} htmlFor="company" data-id="element-93" data-mp="9smugddkyxgl">
            Company
          </label>
          <input id="company" name="company" autoComplete="organization" className={fieldClass} placeholder="Company name" data-id="element-94" data-mp="3s6nfucoxf0ja" />
        </div>
        <div data-id="element-95" data-mp="oq6vxab9qai0">
          <label className={labelClass} htmlFor="email" data-id="element-96" data-mp="opnedupj5o4i">
            Email
          </label>
          <input id="email" name="email" type="email" required autoComplete="email" className={fieldClass} placeholder="you@company.com" data-id="element-97" data-mp="spxkr8h8igjw" />
        </div>
        <div data-id="element-98" data-mp="2zh6shtg5y9tx">
          <label className={labelClass} htmlFor="phone" data-id="element-99" data-mp="2zgdqf5s5g8i4">
            Phone
          </label>
          <input id="phone" name="phone" type="tel" autoComplete="tel" className={fieldClass} placeholder="(555) 555-5555" data-id="element-100" data-mp="34gje6avataux" />
        </div>
        <div className="sm:col-span-2" data-id="element-101" data-mp="3exvm7rzy7w47">
          <label className={labelClass} htmlFor="cargo" data-id="element-102" data-mp="3ewu08she9p7p">
            Cargo type
          </label>
          <select id="cargo" name="cargo" required defaultValue="" className={fieldClass} data-id="element-103" data-mp="3av8rnjbw7x3b">
            <option value="" disabled data-id="element-104" data-mp="36034yiao2ee6">
              Select cargo type
            </option>
            {cargoTypes.map(type => <option key={type} value={type} data-id="element-105" data-mp="2ht334gy3hpl">
                {type}
              </option>)}
          </select>
        </div>
        <div data-id="element-106" data-mp="3tmjenpehzppx">
          <label className={labelClass} htmlFor="origin" data-id="element-107" data-mp="3tm9n3xdt1ai4">
            Origin
          </label>
          <input id="origin" name="origin" required className={fieldClass} placeholder="Lakeland, FL" data-id="element-108" data-mp="3oo841cgdpb6n" />
        </div>
        <div data-id="element-109" data-mp="2i6kyz2ote5fq">
          <label className={labelClass} htmlFor="destination" data-id="element-110" data-mp="2i6bajjjf46c5">
            Destination
          </label>
          <input id="destination" name="destination" required className={fieldClass} placeholder="Newark, NJ" data-id="element-111" data-mp="235nyhp3cgs3l" />
        </div>
        <div data-id="element-112" data-mp="2847hbgmn5nrq">
          <label className={labelClass} htmlFor="pickup" data-id="element-113" data-mp="285sdtbsings8">
            Pickup date
          </label>
          <input id="pickup" name="pickup" type="date" className={fieldClass} data-id="element-114" data-mp="2d2sjvmpd8jjc" />
        </div>
        <div data-id="element-115" data-mp="2hvlqyh8b43yl">
          <label className={labelClass} htmlFor="temperature" data-id="element-116" data-mp="2hvbymmzshkpq">
            Temperature requirements
          </label>
          <input id="temperature" name="temperature" className={fieldClass} placeholder="e.g. 34°F continuous" data-id="element-117" data-mp="2lvvwxtw3mudg" />
        </div>
        <div className="sm:col-span-2" data-id="element-118" data-mp="2vubn9unaxzlw">
          <label className={labelClass} htmlFor="details" data-id="element-119" data-mp="2vvd1g7zm727u">
            Additional details
          </label>
          <textarea id="details" name="details" rows={4} className={`${fieldClass} resize-none`} placeholder="Pallet count, weight, appointment windows, accessorials…" data-id="element-120" data-mp="30s3fyqvrtpr5" />
        </div>
      </div>

      <button type="submit" disabled={status === 'submitting'} className="group mt-8 inline-flex items-center gap-2 rounded-full bg-ice px-8 py-4 text-[13px] font-bold uppercase tracking-[0.1em] text-white transition-colors duration-150 ease-premium hover:bg-ice-600 disabled:opacity-70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ice focus-visible:ring-offset-2" data-id="element-121" data-mp="yefrqfgcixgd">
        {status === 'submitting' ? <>
            <Loader2Icon className="h-4 w-4 animate-spin" data-id="element-122" data-mp="1hb756897iici" />
            Sending…
          </> : <>
            Request a Quote
            <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-premium group-hover:translate-x-1" data-id="element-123" data-mp="1r2bjd5r25z6a" />
          </>}
      </button>

      <p className="mt-4 text-xs leading-relaxed text-ink-soft" data-id="element-124" data-mp="2041a09d48u30">
        Requests are sent to <span className="font-semibold text-ink" data-id="element-125" data-mp="201ot7zj3gprj">{QUOTE_RECIPIENT}</span>.
      </p>
    </form>;
}